import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Icon, Col, Row, Container, Tooltip, LoadingIndicator, IconButton } from '@ctct/ui-react';

import OrderSummary from './OrderSummary';
import { getCurrencySymbol, planNumberEnum } from '../../../utils/pricingUtils';
import { MyAccountApi, DistUI, ContactSvc } from '../api';
import { getCountryCode } from '../api/DistUI';
import { useCancelFlowContext } from '../CancelFlowContext';
import { useDowngradeSavings } from '../hooks';
import './NewPauseOrCancel.scss';

const { getBillingCustomerCurrency, getAccountId } = DistUI;
const { getBillingInfo, getCustomerInfo, getPlanRating } = MyAccountApi;

const cache = {};

async function memoGetPlanRating(props) {
  const propsString = `${props.selectedPlan.id}`;

  if (cache[propsString]) {
    return { ...cache[propsString] };
  }

  const data = await getPlanRating({ ...props });

  cache[propsString] = { ...data };

  return { ...data };
}

const DowngradeCompare = ({ 'data-qe-id': qeId, addOns }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [cancelFlowContext, cancelFlowReducer] = useCancelFlowContext();
  const { savingsAmount, currencyCode } = useDowngradeSavings();
  const { currentPrice, newPrice, hasNotRegistered } = cancelFlowContext;
  const [billingInfoObject, setBillingInfoObject] = useState();
  const [newPlanRating, setNewPlanRating] = useState();
  const [smsTiers, setSMSTiers] = useState();
  const [isUSAccount, setIsUSAccount] = useState();
  const [premiumSendLimit, setPremiumSendLimit] = useState();
  const [standardSendLimit, setStandardSendLimit] = useState();
  const hasAddOns = addOns.length > 0 && addOns.filter((ao) => ao.id !== 'CTCT-SMS-ADDON' && ao.purchased).length > 0;
  const { getContactsCount } = ContactSvc;
  const planGroupEnum = {
    V21: 109,
  };

  useEffect(() => {
    if (isLoading) {
      (async () => {
        const [customerCurrencyCode, { contacts_count: contactCount }, billingInfo, customerId] = await Promise.all([
          getBillingCustomerCurrency(),
          getContactsCount(),
          getBillingInfo(),
          getAccountId(),
        ]);

        const tierUpperBound = Number(
          billingInfo.overview.contact_tier.split('-')[1].replace(/[, ]+/g, ' ').replace(/\s/g, '')
        );

        setStandardSendLimit((tierUpperBound * 12).toLocaleString());
        setPremiumSendLimit((tierUpperBound * 24).toLocaleString());

        const { customer_info: customerInfo } = billingInfo;

        setBillingInfoObject(billingInfo);

        const formattedCustomerInfo = getCustomerInfo({
          customerInfo,
          currency: customerCurrencyCode,
          customerId,
        });
        const countryCode = await getCountryCode();

        setIsUSAccount(countryCode && countryCode.toUpperCase() === 'US');

        const currentPlan = await memoGetPlanRating({
          newAddOnServices: [],
          selectedCurrency: customerCurrencyCode,
          paramPlanGroupId: planGroupEnum.V21,
          selectedPlan: { id: planNumberEnum.PREMIUM },
          contactUsed: contactCount || 0,
          paymentPlanGroupId: 1,
          currentPlanId: planNumberEnum.PREMIUM,
          customer: formattedCustomerInfo,
        });

        const addOnsIds = addOns.filter((ao) => ao.id !== 'CTCT-SMS-ADDON' && ao.purchased).map((ao) => ao.id);

        if (!hasNotRegistered) addOnsIds.push('CTCT-SMS-ADDON');

        setSMSTiers(addOns.find((ao) => ao.id === 'CTCT-SMS-ADDON')?.priceTiers);

        const addOnsServices = addOnsIds && addOnsIds.length > 0 ? addOnsIds : [];
        const newPlan = await memoGetPlanRating({
          newAddOnServices: addOnsServices,
          selectedCurrency: customerCurrencyCode,
          paramPlanGroupId: planGroupEnum.V21,
          selectedPlan: { id: planNumberEnum.STANDARD },
          contactUsed: contactCount || 0,
          paymentPlanGroupId: 1,
          currentPlanId: planNumberEnum.PREMIUM,
          customer: formattedCustomerInfo,
        });

        setNewPlanRating(newPlan);

        let currentPricing = 0;
        let newPricing = 0;

        const { monthlyPrice: currentMonthlyPrice, prepayMonthlyDiscountAmount: currentPrepayDiscount } = currentPlan;
        const fullCurrentPrice = currentMonthlyPrice + currentPrepayDiscount;

        if (fullCurrentPrice % 2 !== 0 || newPlan.monthlyPrice % 2 !== 0) {
          currentPricing = new Intl.NumberFormat(Navigator.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(fullCurrentPrice);
          newPricing = new Intl.NumberFormat(Navigator.language, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(newPlan.monthlyPrice + newPlan.prepayMonthlyDiscountAmount);
        } else {
          currentPricing = fullCurrentPrice;
          newPricing = newPlan.monthlyPrice + newPlan.prepayMonthlyDiscountAmount;
        }

        const { ratePlans } = newPlan;
        const [ratePlan] = ratePlans || [{}];
        const { planType } = ratePlan || {};

        cancelFlowReducer({ type: 'setCurrentPrice', value: currentPricing });
        cancelFlowReducer({ type: 'setNewPrice', value: newPricing });
        cancelFlowReducer({ type: 'setPlanType', value: planType });

        setIsLoading(false);
      })();
    }
  }, []);

  const currentPricing = currentPrice;
  const currentPriceLenth = `${currencyCode}${currentPricing}`.length;
  const newPriceLenth = `${currencyCode}${newPrice}`.length;

  let longNumbers = false;

  if (currentPriceLenth + newPriceLenth > 19) {
    longNumbers = true;
  }

  return (
    <Container className="downgrade-compare">
      <Row>
        <Col widths={['lg']} lg={!hasNotRegistered || hasAddOns ? '9' : '12'}>
          <Container>
            <Row>
              <Col widths={['lg']} lg="8" className="col1-head table-title">
                {savingsAmount && currencyCode && (
                  <Trans
                    i18nKey="cancel-flow.steps.downgrade-cancel.downgrade.compare.title"
                    values={{ savingsAmount: `${getCurrencySymbol(currencyCode)}${savingsAmount}` }}
                  >
                    switch to standard and <strong>save {{ savingsAmount }} each month</strong> on your email
                  </Trans>
                )}
                {hasNotRegistered
                  ? t('cancel-flow.steps.downgrade-cancel.downgrade.compare.title-end-no-sms')
                  : t('cancel-flow.steps.downgrade-cancel.downgrade.compare.title-end-sms')}
              </Col>
              <Col widths={['lg']} lg="2" className="col2-head">
                <b className="plan-name">{t('cancel-flow.steps.downgrade-cancel.downgrade.compare.premium')}</b>
                <br />
                <span className="currency-symbol">{getCurrencySymbol(currencyCode)}</span>
                <span className={`plan-price ${longNumbers ? 'long-numbers' : ''}`}>
                  {currentPricing === 0 ? <LoadingIndicator kind="basicSmall" /> : currentPricing}
                </span>
                <span className="per-month">{t('cancel-flow.steps.downgrade-cancel.downgrade.compare.per-month')}</span>
                <br />
                <span className="plan-status current-plan">
                  {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.current')}
                </span>
              </Col>
              <Col widths={['lg']} lg="2" className="col3-head">
                <b className="plan-name">{t('cancel-flow.steps.downgrade-cancel.downgrade.compare.standard')}</b>
                <br />
                <span className="currency-symbol">{getCurrencySymbol(currencyCode)}</span>
                <span className={`plan-price ${longNumbers ? 'long-numbers' : ''}`}>
                  {newPrice === 0 ? <LoadingIndicator kind="basicSmall" /> : newPrice}
                </span>
                <span className="per-month">{t('cancel-flow.steps.downgrade-cancel.downgrade.compare.per-month')}</span>
                <br />
                <span className="plan-status selected-plan">
                  <Icon glyph="checkCircleFilled" />
                  {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.selected')}
                </span>
              </Col>
            </Row>
            <br />
            {isUSAccount && (
              <Row>
                <Col widths={['lg']} lg="8" className="col1">
                  {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.sms')}
                </Col>
                <Col widths={['lg']} lg="2" className="col2">
                  {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.sms-premium')}
                </Col>
                <Col widths={['lg']} lg="2" className="col3">
                  {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.sms-standard')}
                </Col>
              </Row>
            )}
            <Row>
              <Col widths={['lg']} lg="12" className="col1 colored">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.headers.permissions')}{' '}
                <Icon glyph="personFilled" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.users')}
                <Tooltip
                  data-qe-id="tooltip-users"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.users-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.users-premium')}
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.users-standard')}
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.allowance')}
                <Tooltip
                  data-qe-id="tooltip-allowance"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.allowance-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                {premiumSendLimit}
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                {standardSendLimit}
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.storage')}
                <Tooltip
                  data-qe-id="tooltip-storage"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.storage-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.storage-premium')}
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.storage-standard')}
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="12" className="col1 colored">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.headers.ecommerce')} <Icon glyph="tagFilled" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.revenue')}
                <Tooltip
                  data-qe-id="tooltip-revenue"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.revenue-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                -
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.purchasing')}
                <Tooltip
                  data-qe-id="tooltip-purchase-segmentation"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.purchasing-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                -
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="12" className="col1 colored">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.headers.targeting')}{' '}
                <Icon glyph="lightningFilled" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.engagement')}
                <Tooltip
                  data-qe-id="tooltip-action-segmentation"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.engagement-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                -
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.blocks')}
                <Tooltip
                  data-qe-id="tooltip-content-blocks"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.blocks-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                -
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.gads')}
                <Tooltip
                  data-qe-id="tooltip-gads"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.gads-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                -
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="12" className="col1 colored">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.headers.keep')} <Icon glyph="starFilled" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.smads')}
                <Tooltip
                  data-qe-id="tooltip-smads"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.smads-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.templates')}
                <Tooltip
                  data-qe-id="tooltip-templates"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.templates-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.social-marketing')}
                <Tooltip
                  data-qe-id="tooltip-social-marketing"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.social-marketing-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.event')}
                <Tooltip
                  data-qe-id="tooltip-event"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.event-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.surveys')}
                <Tooltip
                  data-qe-id="tooltip-surveys"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.surveys-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.integrations')}
                <Tooltip
                  data-qe-id="tooltip-integrations"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.integrations-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.signup')}
                <Tooltip
                  data-qe-id="tooltip-signup"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.signup-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.reach')}
                <Tooltip
                  data-qe-id="tooltip-reach"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.reach-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.calendar')}
                <Tooltip
                  data-qe-id="tooltip-calendar"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.calendar-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.welcome')}
                <Tooltip
                  data-qe-id="tooltip-welcome"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.welcome-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.ab')}
                <Tooltip
                  data-qe-id="tooltip-ab"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.ab-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.smscheduling')}
                <Tooltip
                  data-qe-id="tooltip-smscheduling"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.smscheduling-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.automation')}
                <Tooltip
                  data-qe-id="tooltip-automation"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.automation-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.resend')}
                <Tooltip
                  data-qe-id="tooltip-resend"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.resend-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.drilldown')}
                <Tooltip
                  data-qe-id="tooltip-drilldown"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.drilldown-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
            <Row>
              <Col widths={['lg']} lg="8" className="col1">
                {t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.contact-segmentation')}
                <Tooltip
                  data-qe-id="tooltip-contact-segmentation"
                  className="tooltip"
                  content={t('cancel-flow.steps.downgrade-cancel.downgrade.compare.items.contact-segmentation-tooltip')}
                  placement="above"
                >
                  <IconButton className="tooltip-button" glyph="infoCircle" size="small" />
                </Tooltip>
              </Col>
              <Col widths={['lg']} lg="2" className="col2">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
              <Col widths={['lg']} lg="2" className="col3">
                <Icon glyph="checkCircleFilled" kind="success" />
              </Col>
            </Row>
          </Container>
        </Col>
        {(!hasNotRegistered || hasAddOns) && (
          <OrderSummary t={t} qeId={qeId} rating={newPlanRating} billingInfo={billingInfoObject} smsTiers={smsTiers} />
        )}
      </Row>
    </Container>
  );
};

DowngradeCompare.propTypes = {
  'data-qe-id': PropTypes.string.isRequired,
  addOns: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
};

DowngradeCompare.defaultProps = {
  addOns: [],
};

export default DowngradeCompare;
